import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { fetchConfig, yieldToMainThread, onAnimationEnd } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * B2B-style variant quantity table: local qty inputs, line totals, add multiple to cart.
 */
class QuickAddVariantMatrix extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onInput);
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    this.#refreshTotals();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onInput);
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
  }

  /** @param {Event} e */
  #onQuantitySelectorUpdate = (e) => {
    if (!this.contains(/** @type {Node} */ (e.target))) return;
    this.#refreshTotals();
  };

  /** @param {Event} e */
  #onClick = (e) => {
    const target = /** @type {HTMLElement} */ (e.target);

    if (target.closest('[data-action="remove-all"]')) {
      e.preventDefault();
      this.#resetMatrixInputs();
      return;
    }

    if (target.closest('[data-action="add-to-cart"]')) {
      e.preventDefault();
      this.#submitCart();
    }
  };

  /** @param {Event} e */
  #onInput = (e) => {
    if (e.target instanceof HTMLInputElement && e.target.classList.contains('product-variant-qty-table__qty-input')) {
      this.#refreshTotals();
    }
  };

  #resetMatrixInputs() {
    this.querySelectorAll('.product-variant-qty-table__qty-input').forEach((el) => {
      if (!(el instanceof HTMLInputElement) || el.disabled) return;
      el.value = '0';
      const comp = el.closest('quantity-selector-component');
      if (comp && 'updateButtonStates' in comp && typeof comp.updateButtonStates === 'function') {
        comp.updateButtonStates();
      }
    });
    this.#refreshTotals();
  }

  #currency() {
    return this.dataset.currency || /** @type {any} */ (window).Shopify?.currency?.active || 'USD';
  }

  /** Shopify `shop.money_format` / `shop.money_with_currency_format` from data-money-format */
  #moneyFormat() {
    const raw = this.dataset.moneyFormat;
    if (raw && raw.trim() !== '') return raw.trim();
    return '{{amount}}';
  }

  #formatCents(cents) {
    return formatMoney(cents, this.#moneyFormat(), this.#currency());
  }

  #refreshTotals() {
    let totalQty = 0;
    let totalCents = 0;

    this.querySelectorAll('tr[data-variant-id][data-price]').forEach((row) => {
      const price = parseInt(row.getAttribute('data-price') || '0', 10);
      const input = row.querySelector('.product-variant-qty-table__qty-input');
      const lineEl = row.querySelector('[data-line-total]');
      if (!(input instanceof HTMLInputElement) || !lineEl) return;

      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      totalQty += qty;
      const lineCents = price * qty;
      totalCents += lineCents;

      lineEl.textContent = this.#formatCents(lineCents);
    });

    const qtyEl = this.querySelector('[data-total-qty]');
    const moneyEl = this.querySelector('[data-total-money]');
    if (qtyEl) qtyEl.textContent = String(totalQty);
    if (moneyEl) {
      moneyEl.textContent = this.#formatCents(totalCents);
    }

    this.#refreshInventoryDisplays();
  }

  /**
   * Updates "selected / total" copy and stock colors (aligned with product-inventory block).
   */
  #refreshInventoryDisplays() {
    const threshold = parseInt(this.dataset.inventoryThreshold || '10', 10);

    this.querySelectorAll('[data-inventory-tracked-row]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const row = el.closest('tr');
      const input = row?.querySelector('.product-variant-qty-table__qty-input');
      const textEl = el.querySelector('[data-inventory-text]');
      if (!(input instanceof HTMLInputElement) || !textEl) return;

      const total = parseInt(el.dataset.inventoryTotal || '0', 10);
      const selected = Math.max(0, parseInt(input.value, 10) || 0);
      const suffix = el.dataset.inventoryUnitsSuffix || '';
      textEl.textContent = `${selected}/${total}${suffix}`;

      const continueOos = el.dataset.inventoryContinueOos === 'true';
      const remaining = total - selected;

      /** @type {'in_stock' | 'low' | 'out_of_stock'} */
      let status = 'in_stock';

      if (!continueOos && remaining < 0) {
        status = 'out_of_stock';
      } else if (!continueOos && remaining > 0 && remaining <= threshold) {
        status = 'low';
      } else if (!continueOos && remaining > threshold) {
        status = 'in_stock';
      } else if (!continueOos && remaining === 0 && total > 0) {
        status = 'in_stock';
      } else if (continueOos) {
        status = 'in_stock';
      }

      this.#setInventoryTextStatus(textEl, status);
    });
  }

  /**
   * @param {Element} textEl
   * @param {'in_stock' | 'low' | 'out_of_stock'} status
   */
  #setInventoryTextStatus(textEl, status) {
    const states = ['in_stock', 'low', 'out_of_stock'];
    for (const s of states) {
      textEl.classList.remove(`product-variant-qty-table__inventory-text--${s}`);
    }
    textEl.classList.add(`product-variant-qty-table__inventory-text--${status}`);
  }

  async #submitCart() {
    /** @type {{ id: number; quantity: number }[]} */
    const items = [];

    this.querySelectorAll('tr[data-variant-id]').forEach((row) => {
      const id = parseInt(row.getAttribute('data-variant-id') || '0', 10);
      const input = row.querySelector('.product-variant-qty-table__qty-input');
      if (!(input instanceof HTMLInputElement) || input.disabled) return;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      if (qty > 0 && id) items.push({ id, quantity: qty });
    });

    const errEl = this.querySelector('[data-error]');
    const selectMsg = this.dataset.selectQtyMessage || 'Select at least one quantity';

    if (items.length === 0) {
      if (errEl) {
        errEl.textContent = selectMsg;
        errEl.classList.remove('visually-hidden');
      }
      return;
    }
    if (errEl) {
      errEl.textContent = '';
      errEl.classList.add('visually-hidden');
    }

    const addBtn = this.querySelector('[data-action="add-to-cart"]');
    const defLabel = this.dataset.addToCartLabel || 'Add to cart';
    const addingLabel = this.dataset.addingLabel || '...';

    if (addBtn instanceof HTMLButtonElement) {
      addBtn.disabled = true;
      this.#setMatrixAddCartLabel(addBtn, addingLabel);
    }

    try {
      const body = JSON.stringify({ items });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body }),
      });

      const data = await response.json();

      if (!response.ok) {
        const message = typeof data.message === 'string' ? data.message : data.description || 'Error';
        document.dispatchEvent(
          new CartErrorEvent(
            this.id || 'quick-add-variant-matrix',
            message,
            typeof data.description === 'object' && data.description ? data.description : {},
            data.errors || {}
          )
        );
        if (errEl) {
          errEl.textContent = message;
          errEl.classList.remove('visually-hidden');
        }
        return;
      }

      document.dispatchEvent(
        new CartAddEvent(data, this.id || 'quick-add-variant-matrix', {
          source: 'quick-add-variant-matrix',
          productId: this.dataset.productId,
        })
      );

      this.#resetMatrixInputs();

      if (addBtn instanceof HTMLButtonElement) {
        this.#setMatrixAddCartLabel(addBtn, defLabel);
        await this.#playMatrixAddToCartAnimation(addBtn);
      }

      const dialog = this.closest('quick-add-dialog');
      if (dialog && typeof dialog.closeDialog === 'function') {
        dialog.closeDialog();
      }
    } catch {
      if (errEl) {
        errEl.textContent = 'Error';
        errEl.classList.remove('visually-hidden');
      }
    } finally {
      if (addBtn instanceof HTMLButtonElement) {
        addBtn.disabled = false;
        this.#setMatrixAddCartLabel(addBtn, defLabel);
        addBtn.removeAttribute('data-added');
      }
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} label
   */
  #setMatrixAddCartLabel(button, label) {
    const span = button.querySelector('[data-add-cart-label]');
    if (span) span.textContent = label;
  }

  /**
   * Same success animation as `add-to-cart-component` / `.add-to-cart-button[data-added]`.
   * @param {HTMLButtonElement} button
   */
  async #playMatrixAddToCartAnimation(button) {
    if (button.dataset.added !== 'true') button.dataset.added = 'true';
    await yieldToMainThread();
    await onAnimationEnd(button);
    await new Promise((r) => setTimeout(r, 800));
    button.removeAttribute('data-added');
  }
}

if (!customElements.get('quick-add-variant-matrix')) {
  customElements.define('quick-add-variant-matrix', QuickAddVariantMatrix);
}
